import { Quest, QUESTS, QuestStatus } from '../data/quests';
import { AudioSystem } from './AudioSystem';
import { SaveSystem } from './SaveSystem';

export type QuestChangeCallback = () => void;

export class QuestSystem {
  private quests: Quest[];
  private onChange?: QuestChangeCallback;

  constructor(onChange?: QuestChangeCallback) {
    this.onChange = onChange;
    const save = SaveSystem.load();

    this.quests = QUESTS.map((q) => {
      const isCompleted = save.completedQuests.includes(q.id);
      const progress = save.questProgress[q.id] ?? (isCompleted ? q.maxProgress : 0);
      let status: QuestStatus = q.status;
      if (isCompleted) status = 'completed';
      else if (progress > 0 && status === 'locked') status = 'active';

      return { ...q, status, progress };
    });
  }

  getQuests(): Quest[] {
    return this.quests.map((q) => ({ ...q }));
  }

  getActiveQuests(): Quest[] {
    return this.quests.filter((q) => q.status === 'active');
  }

  getQuestsByZone(zone: number): Quest[] {
    return this.quests.filter((q) => q.zone === zone);
  }

  getQuestById(id: string): Quest | undefined {
    return this.quests.find((q) => q.id === id);
  }

  activateQuest(id: string): void {
    const quest = this.quests.find((q) => q.id === id);
    if (!quest || quest.status === 'completed') return;
    if (quest.status !== 'active') {
      quest.status = 'active';
      this.persist();
    }
    this.onChange?.();
  }

  /** Pastikan satu misi berikutnya dalam rantai zona aktif dan tampil di HUD. */
  syncZoneQuestChain(order: readonly string[]): Quest | null {
    let current: Quest | null = null;
    let foundCurrent = false;
    let changed = false;

    for (const id of order) {
      const quest = this.quests.find((q) => q.id === id);
      if (!quest || quest.status === 'completed') continue;

      if (!foundCurrent) {
        foundCurrent = true;
        current = quest;
        if (quest.status !== 'active') {
          quest.status = 'active';
          changed = true;
        }
      } else if (quest.status === 'active') {
        quest.status = 'locked';
        changed = true;
      }
    }

    if (changed) this.persist();
    return current;
  }

  getCurrentZoneQuest(order: readonly string[]): Quest | null {
    for (const id of order) {
      const quest = this.quests.find((q) => q.id === id);
      if (quest && quest.status !== 'completed') return quest;
    }
    return null;
  }

  updateProgress(id: string, progress: number): void {
    const quest = this.quests.find((q) => q.id === id);
    if (!quest || quest.status === 'completed') return;

    quest.progress = Math.min(Math.max(0, progress), quest.maxProgress);
    if (quest.status === 'locked') quest.status = 'active';

    if (quest.progress >= quest.maxProgress) {
      this.completeQuest(id);
      return;
    }

    this.persist();
    this.onChange?.();
  }

  completeQuest(id: string): void {
    const quest = this.quests.find((q) => q.id === id);
    if (!quest || quest.status === 'completed') return;

    quest.status = 'completed';
    quest.progress = quest.maxProgress;

    const save = SaveSystem.load();
    const completed = [...save.completedQuests];
    if (!completed.includes(id)) completed.push(id);

    const questProgress = { ...save.questProgress, [id]: quest.maxProgress };
    SaveSystem.save({ completedQuests: completed, questProgress });
    AudioSystem.playSfx('QUEST');
    this.onChange?.();
  }

  getStatus(id: string): QuestStatus {
    return this.getQuestById(id)?.status ?? 'locked';
  }

  private persist(): void {
    const save = SaveSystem.load();
    const questProgress = { ...save.questProgress };
    this.quests.forEach((q) => {
      if (q.status !== 'completed') questProgress[q.id] = q.progress;
    });
    SaveSystem.save({ questProgress });
  }
}
